import React, { FunctionComponent, ReactNode } from 'react'
import { useWindowDimensions, View } from 'react-native'

import { noop } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { BackButtonV2Type as BackButtonType, BackButtonV2 } from '../../atoms/backButton/BackButtonV2'
import { DropdownButton, DropdownMenuItem } from '../../atoms/dropdownButton/DropdownButton'
import {
  TypeAheadCustomOptionsConfig,
  ValueFetchMode,
} from '../../atoms/formElements/typeAhead/TypeAheadCustomOptionsConfig'
import { MenuDotsIcon } from '../../atoms/icons/MenuDotsIcon'
import { AICoachToggle, AIConversationToggleRow } from '../../molecules/aiCoachToggle/AICoachToggle'
import { ProfileCard as ProfileCardComponent } from '../../molecules/profileCard/ProfileCard'
import {
  ClientDetailsTabNavigation,
  ClientDetailsTabType,
} from '../../organisms/clientDetailsTabNavigation/ClientDetailsTabNavigation'
import { ClientProfileHeaderBar } from '../../organisms/clientProfileHeaderBar/ClientProfileHeaderBar'
import { Menu, MenuItemProps } from '../../organisms/menu/Menu'
import { MessagingHub } from '../../organisms/messagingHub/MessagingHub'
import { Modal } from '../../organisms/modal/Modal'
import { BodyTextSize } from '../../styles/typeStyles'
import { Details } from '../../ui-models/messages/Details'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

const ClientDetailsContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: !theme.breakpoints.isMinWidthLaptop ? 'column' : 'row',
}))

const LeftSide = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  zIndex: theme.breakpoints.isMinWidthLaptop ? 0 : 1,
  ...(theme.breakpoints.isMinWidthLaptop && {
    flex: '0 0 auto',
  }),
}))

const RightSide = styled.View({
  flex: 1,
})

const ProfileCardContainer = styled.View<{ theme: ThemeType }>(({ theme: { colors, breakpoints, spacing } }) => ({
  backgroundColor: colors.backgroundSection,
  paddingLeft: spacing['40px'],
  paddingTop: spacing['24px'],
  paddingRight: spacing['40px'],
  gap: spacing['16px'],
  ...(breakpoints.isMinWidthLaptop && {
    borderBottomWidth: 1,
    borderBottomColor: colors.borderDefault,
    borderStyle: 'solid',
    height: 168,
    paddingRight: spacing['0px'],
  }),
}))

const ProfileCardBlock = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  width: !theme.breakpoints.isMinWidthLaptop ? '100%' : '288px',
  flexGrow: 1,
}))

const TabNavigationContainer = styled.View({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
})

const ContentOutlet = styled.View<{ theme: ThemeType; hasNoMargin?: boolean }>(
  ({ theme: { spacing }, hasNoMargin }) => ({
    margin: hasNoMargin ? '0px' : spacing['40px'],
    zIndex: 5,
  }),
)

const ScrollHeightCompensation = styled.View<{ height: number }>(({ height }) => ({
  height,
  flexShrink: 0,
}))

const ActionsMenuMessagingCTAContainer = styled.View({
  flexDirection: 'row',
  gap: 8,
  justifyContent: 'space-between',
})

const NavigationSection = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  width: '100%',
  padding: !theme.breakpoints.isMinWidthLaptop ? '24px 40px 0 40px' : '0 40px 0 40px',
  backgroundColor: theme.colors.backgroundSection,
  borderBottomWidth: 1,
  borderBottomColor: theme.colors.borderDefault,
  borderStyle: 'solid',
  zIndex: 10,
  ...(theme.breakpoints.isMinWidthLaptop && {
    height: '168px',
  }),
}))

const BackButtonContainer = styled.View({
  height: '38px',
  width: '38px',
})

type ClientDetailsProps = {
  content: React.ReactElement
  tabs: ClientDetailsTabType[]
  onBackPress?: () => void
  dropdownOption: MenuItemProps[]
  modalProps?: {
    modalContents: ReactNode
    onRequestClose: () => void
    onCloseEnd: () => void
  }
  clientDetails?: Details | null
  messagingPopover: React.FC<{ closePopover: () => void; className?: string }>
  forceMessagingHubToOpenPopover: boolean
  onMessagesPopoverOpen: () => void
  numUnreadMessages: number
  episodePickerDropdownItems: DropdownMenuItem[]
  selectedEpisodeId?: string
  contentHasNoMargin?: boolean
  showSubNav: boolean
  showExportButton?: boolean
  onExportClientRecords?: () => void
  /** Lyra AI conversations the provider can turn on or off for this client. Vertical layout only; hidden when empty. */
  aiConversationToggleRows?: AIConversationToggleRow[]
  showEmail?: boolean
  riskBadgeSlot?: React.ReactNode
  useHorizontalHeader?: boolean
  caregiverSubtitle?: React.ReactNode
  onSchedulePress?: () => void
  hideScheduleSessionButton?: boolean
  stickyTopOffset?: number
  isLocked?: boolean
  containerRef?: (node: View | null) => void
  isCollapsed?: boolean
  compensationHeight?: number
}

export const ClientDetails: FunctionComponent<ClientDetailsProps> = ({
  tabs,
  content,
  onBackPress,
  dropdownOption,
  modalProps,
  clientDetails,
  messagingPopover,
  forceMessagingHubToOpenPopover,
  onMessagesPopoverOpen,
  numUnreadMessages,
  episodePickerDropdownItems,
  selectedEpisodeId,
  contentHasNoMargin,
  showSubNav,
  showExportButton,
  onExportClientRecords,
  aiConversationToggleRows = [],
  showEmail = false,
  riskBadgeSlot,
  useHorizontalHeader = false,
  caregiverSubtitle,
  onSchedulePress,
  hideScheduleSessionButton = false,
  stickyTopOffset,
  isLocked = false,
  containerRef: clientProfileHeaderRef,
  isCollapsed: isClientProfileHeaderCollapsed = false,
  compensationHeight: clientProfileHeaderCompensationHeight = 0,
}) => {
  const { colors, spacing } = useTheme()
  const { height } = useWindowDimensions()
  const bottomAndTopMargin = 50

  if (!showSubNav) {
    return (
      <ContentOutlet hasNoMargin={contentHasNoMargin} testID={tID('ClientDetails-content')}>
        {content}
      </ContentOutlet>
    )
  }

  if (useHorizontalHeader) {
    return (
      <>
        <ClientProfileHeaderBar
          clientDetails={clientDetails ?? null}
          tabs={tabs}
          dropdownOptions={dropdownOption}
          onBackPress={onBackPress}
          messagingPopover={messagingPopover}
          forceMessagingHubToOpenPopover={forceMessagingHubToOpenPopover}
          onMessagesPopoverOpen={onMessagesPopoverOpen}
          numUnreadMessages={numUnreadMessages}
          selectedEpisodeId={selectedEpisodeId}
          showEmail={showEmail}
          riskBadgeSlot={riskBadgeSlot}
          caregiverSubtitle={caregiverSubtitle}
          onSchedulePress={onSchedulePress}
          hideScheduleSessionButton={hideScheduleSessionButton}
          stickyTopOffset={stickyTopOffset}
          containerRef={clientProfileHeaderRef}
          isCollapsed={isClientProfileHeaderCollapsed}
        />
        {aiConversationToggleRows.length > 0 && (
          <ProfileCardContainer>
            <AICoachToggle rows={aiConversationToggleRows} />
          </ProfileCardContainer>
        )}
        <ContentOutlet hasNoMargin={contentHasNoMargin} testID={tID('ClientDetails-content')}>
          {content}
        </ContentOutlet>
        <ScrollHeightCompensation
          height={clientProfileHeaderCompensationHeight}
          testID={tID('ClientDetails-scrollHeightCompensation')}
        />
        <Modal
          visible={!!modalProps?.modalContents}
          modalContents={modalProps?.modalContents || ''}
          onRequestClose={modalProps?.onRequestClose || noop}
          onCloseEnd={modalProps?.onCloseEnd}
          scrollable
          isHeaderTransparent
          scrollableStyle={{ height: height - bottomAndTopMargin }}
        />
      </>
    )
  }

  return (
    <ClientDetailsContainer>
      <LeftSide>
        <ProfileCardContainer>
          <BackButtonContainer>
            {onBackPress && (
              <BackButtonV2 onPress={onBackPress} backButtonType={BackButtonType.OATMEAL} disabled={isLocked} />
            )}
          </BackButtonContainer>
          <ProfileCardBlock>
            <ProfileCardComponent
              clientDetails={clientDetails ?? null}
              showExportButton={showExportButton}
              onExportClientRecords={onExportClientRecords}
              showEmail={showEmail}
              isLoading={false}
              riskBadgeSlot={riskBadgeSlot}
            />
          </ProfileCardBlock>
          {aiConversationToggleRows.length > 0 && <AICoachToggle rows={aiConversationToggleRows} />}
        </ProfileCardContainer>
      </LeftSide>
      <RightSide>
        <NavigationSection>
          {episodePickerDropdownItems?.length > 1 && (
            <DropdownButton
              testID={'ClientDetails-episode-picker'}
              customTextColor={colors.secondaryButtonTextActive}
              customTextSize={BodyTextSize.CAPTION}
              containerStyle={{ paddingBottom: spacing['16px'], zIndex: 15 }}
              dropdownWidth='318px'
              dropdownItems={episodePickerDropdownItems}
              style={{
                paddingLeft: spacing['16px'],
                paddingRight: spacing['12px'],
                height: '30px',
                backgroundColor: colors.backgroundSection,
              }}
              value={selectedEpisodeId}
              customOptionsConfig={
                new TypeAheadCustomOptionsConfig(
                  episodePickerDropdownItems,
                  undefined,
                  undefined,
                  'text',
                  'id',
                  ValueFetchMode.ID,
                )
              }
              selectedItemStyle={null}
            />
          )}
          <TabNavigationContainer>
            <ClientDetailsTabNavigation tabs={tabs} />
            <ActionsMenuMessagingCTAContainer>
              <MessagingHub
                messagingPopover={messagingPopover}
                forceMessagingHubToOpenPopover={forceMessagingHubToOpenPopover}
                onMessagesPopoverOpen={onMessagesPopoverOpen}
                numUnreadMessages={numUnreadMessages}
                selectedEpisodeId={selectedEpisodeId}
              />
              <Menu
                testID={'ClientDetails-actions-menu'}
                customMenuIcon={<MenuDotsIcon vertical />}
                dropdownWidth={265}
                menuItems={dropdownOption}
                horizontalPosition='right'
                menuItemStyle={{
                  paddingLeft: spacing['24px'],
                  paddingRight: spacing['24px'],
                }}
                dropdownStyle={{
                  top: '114%',
                  right: '-50%',
                }}
              />
            </ActionsMenuMessagingCTAContainer>
          </TabNavigationContainer>
        </NavigationSection>
        <ContentOutlet hasNoMargin={contentHasNoMargin} testID={tID('ClientDetails-content')}>
          {content}
        </ContentOutlet>
      </RightSide>
      <Modal
        visible={!!modalProps?.modalContents}
        modalContents={modalProps?.modalContents || ''}
        onRequestClose={modalProps?.onRequestClose || noop}
        onCloseEnd={modalProps?.onCloseEnd}
        scrollable
        isHeaderTransparent
        scrollableStyle={{ height: height - bottomAndTopMargin }}
      />
    </ClientDetailsContainer>
  )
}
